// Avisos: todo lo que les llega a los clientes por WhatsApp, en un solo lugar.
//
// Antes esto vivía repartido: un botón en rutas, otro en el panel de inicio, y
// los avisos automáticos sin ninguna pantalla donde comprobar que habían
// salido. Esa falta costó caro: api_comanda_cierra_hoy falló 502 veces durante
// semanas sin que nadie lo notara, porque el único rastro estaba en el
// registro del servidor.

import { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import LayoutPages from './../../components/LayoutPages/LayoutPages';
import TarjetaCampana from '../../sections/avisos/TarjetaCampana';
import TarjetaDifusion from '../../sections/avisos/TarjetaDifusion';
import useCampanas from '../../sections/avisos/useCampanas';
import useDifusion from '../../sections/avisos/useDifusion';
import { Aparece } from '../../sections/dashboard/motion';
import './Avisos.scss';

/* Un color por campaña: se distinguen antes de leerlas, y el tono dice algo
   —el verde es rutina, el ámbar es que se está venciendo, el rojo es que ya
   se fue—. */
const COLORES = { 'sin-elegir': 'primary', 'por-vencer': 'warning', vencidos: 'error' };

const AvisosPage = () => {
  const { campanas, cargando, error, recargar, enviar } = useCampanas();
  const difusion = useDifusion();

  /* Que campañas se estan mandando ahora, con la marca del ultimo envio que
     tenian al arrancar: cuando esa marca cambia es que ya termino. */
  const [enCurso, setEnCurso] = useState({});
  const enCursoRef = useRef(enCurso);
  enCursoRef.current = enCurso;

  /* El "mandando" se enciende al tocar el boton, no cuando el servidor
     contesta. Esperar la respuesta dejaba la pantalla muda unos segundos, y
     en esos segundos se aprieta de nuevo: asi se dispararon tres envios
     seguidos de la misma campaña. */
  const mandar = (clave, marcaPrevia) => {
    setEnCurso((c) => ({ ...c, [clave]: marcaPrevia ?? null }));
    return enviar(clave).catch((e) => {
      setEnCurso((c) => { const copia = { ...c }; delete copia[clave]; return copia; });
      throw e;
    });
  };

  /* Mientras haya una campaña en curso se vuelve a preguntar cada cinco
     segundos. El envio puede tardar dos minutos y no hay nada que esperar en
     la conexion: el resultado llega al historial. */
  useEffect(() => {
    if (Object.keys(enCurso).length === 0) return undefined;
    const t = setInterval(() => recargar(), 5000);
    return () => clearInterval(t);
  }, [enCurso, recargar]);

  /* Al llegar datos nuevos, se sacan de la lista las que ya terminaron. */
  useEffect(() => {
    if (!campanas || Object.keys(enCursoRef.current).length === 0) return;
    const terminadas = campanas.filter(
      (c) => c.clave in enCursoRef.current && c.ultimoEnvio !== enCursoRef.current[c.clave]);
    if (terminadas.length === 0) return;
    setEnCurso((actual) => {
      const copia = { ...actual };
      terminadas.forEach((c) => delete copia[c.clave]);
      return copia;
    });
  }, [campanas]);

  return (
    <LayoutPages cssClass="pageAvisos">
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>WhatsApp</Typography>
        <Typography variant="h2">Avisos a clientes</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: .5, maxWidth: 720 }}>
          Cada campaña sale sola a su hora. Aquí se ve a cuántos les toca ahora mismo,
          cómo salió la última vez y se puede volver a mandar sin esperar.
        </Typography>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}
      {difusion.error && <Alert severity="warning" sx={{ mb: 3 }}>{difusion.error}</Alert>}

      {/* Va primero: es lo que se usa tres veces por semana. Las campañas de
          abajo salen solas y solo se tocan cuando algo falló. */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6, lg: 5 }}>
          {difusion.cargando
            ? <Skeleton variant="rounded" height={520} sx={{ borderRadius: 2 }} />
            : <Aparece i={0}>
                <TarjetaDifusion
                  estado={difusion.estado}
                  onDescargar={difusion.descargar}
                  onRecargar={difusion.recargar}
                />
              </Aparece>}
        </Grid>
      </Grid>

      {cargando &&
        <Grid container spacing={2.5}>
          {[0, 1, 2].map((i) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
              <Skeleton variant="rounded" height={380} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      }

      {campanas &&
        <Grid container spacing={2.5}>
          {campanas.map((campana, i) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={campana.clave}>
              <Aparece i={i}>
                <TarjetaCampana
                  campana={campana}
                  onEnviar={mandar}
                  enviando={campana.clave in enCurso}
                  color={COLORES[campana.clave] || 'primary'}
                />
              </Aparece>
            </Grid>
          ))}
        </Grid>
      }

      {campanas && campanas.length === 0 && !error &&
        <Alert severity="info">Todavía no hay campañas configuradas.</Alert>
      }
    </LayoutPages>
  );
};

export default AvisosPage;
